import React, { useState, useEffect, useCallback } from "react";
import {
  Layout,
  Card,
  Table,
  Button,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  Input,
  Modal,
  Form,
  Select,
  Avatar,
  Tooltip,
} from "antd";
import {
  FaBuilding,
  FaSearch,
  FaEdit,
  FaPlus,
  FaEnvelope,
  FaUserTie,
  FaUsers,
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Api from "../api/Api";
import "./Direcoes.css"; // Opcional

const { Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

/* ─────────────────────────────────────────────────────────────────────────────
   MODAL DE ADICIONAR/EDITAR DIREÇÃO
───────────────────────────────────────────────────────────────────────────── */
const DirecaoFormModal = ({
  open,
  onClose,
  onSuccess,
  editingDirecao,
  userStored,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [usuarios, setUsuarios] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    if (open) {
      loadUsuarios();
      if (editingDirecao) {
        form.setFieldsValue({
          descricao: editingDirecao.descricao,
          email: editingDirecao.email,
          id_direcetor: editingDirecao.id_direcetor,
        });
      } else {
        form.resetFields();
      }
    }
  }, [open, editingDirecao, form]);

  const loadUsuarios = async () => {
    try {
      setLoadingUsers(true);
      // Carrega usuários de uma direção específica? Podemos usar ListUserDirecao passando id_direcao do usuário logado
      // Mas para selecionar diretor, melhor carregar todos os usuários? Vamos assumir que há um endpoint ListUser sem filtro ou usar ListUserDirecao com id_direcao do usuário logado.
      // No código anterior, Api.ListUserDirecao({id_direcao: userStored.direcao}) retornava usuários daquela direção.
      // Para escolher diretor de qualquer direção, seria ideal ter ListAllUsers. Vou usar ListUserDirecao com id_direcao do usuário logado como fallback, mas pode não ser completo.
      // Sugiro usar um endpoint hipotético Api.ListAllUsers(). Adapte conforme necessário.
      const payload = { id_direcao: userStored.direcao };
      const response = await Api.ListUserDirecao(payload);
      setUsuarios(Array.isArray(response) ? response : []);
    } catch {
      toast.error("Erro ao carregar usuários.");
      setUsuarios([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const payload = {
        descricao: values.descricao,
        email: values.email,
        id_direcetor: values.id_direcetor || null,
      };

      if (editingDirecao) {
        await Api.AlterDirecao({
          ...payload,
          id: editingDirecao.id,
        });
        toast.success("Direção atualizada com sucesso!");
      } else {
        await Api.AddDirecao(payload);
        toast.success("Direção criada com sucesso!");
      }

      form.resetFields();
      onSuccess();
      onClose();
    } catch (error) {
      if (error.errorFields) {
        toast.error("Preencha todos os campos obrigatórios.");
      } else {
        toast.error(
          editingDirecao
            ? "Erro ao atualizar direção."
            : "Erro ao criar direção."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={550}
      title={
        <Space>
          <FaBuilding style={{ color: "#1677ff", fontSize: 20 }} />
          <span style={{ fontWeight: 600, fontSize: 18 }}>
            {editingDirecao ? "Editar Direção" : "Nova Direção"}
          </span>
        </Space>
      }
      footer={[
        <Button key="cancel" onClick={onClose} style={{ borderRadius: 6 }}>
          Cancelar
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          loading={loading}
          style={{ borderRadius: 6, fontWeight: 500 }}
        >
          {editingDirecao ? "Atualizar" : "Criar Direção"}
        </Button>,
      ]}
      bodyStyle={{ padding: "24px 24px 0" }}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label={<span style={{ fontWeight: 500 }}>Descrição</span>}
          name="descricao"
          rules={[{ required: true, message: "Campo obrigatório" }]}
        >
          <Input placeholder="Ex: DIREÇÃO DE ENGENHARIA" size="large" />
        </Form.Item>

        <Form.Item
          label={<span style={{ fontWeight: 500 }}>Email</span>}
          name="email"
          rules={[
            { required: true, message: "Campo obrigatório" },
            { type: "email", message: "Email inválido" },
          ]}
        >
          <Input placeholder="exemplo@technext.ao" size="large" />
        </Form.Item>

        <Form.Item
          label={<span style={{ fontWeight: 500 }}>Diretor Responsável</span>}
          name="id_direcetor"
        >
          <Select
            placeholder="Selecione um diretor"
            loading={loadingUsers}
            size="large"
            allowClear
            showSearch
            filterOption={(input, option) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            }
          >
            {usuarios.map((user) => (
              <Option key={user.id} value={user.id}>
                {user.nome}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   PÁGINA PRINCIPAL DE DIREÇÕES
───────────────────────────────────────────────────────────────────────────── */
const Direcoes = () => {
  const [direcoes, setDirecoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingDirecao, setEditingDirecao] = useState(null);

  const userStored = JSON.parse(localStorage.getItem("user")) || {};

  useEffect(() => {
    document.title = "Gestão de Direções | Dashboard";
  }, []);

  const loadDirecoes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await Api.ListDirecao();
      const list = Array.isArray(response) ? response : [];
      setDirecoes(list);
    } catch {
      toast.error("Erro ao carregar direções.");
      setDirecoes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDirecoes();
  }, [loadDirecoes]);

  const openFormModal = (direcao = null) => {
    setEditingDirecao(direcao);
    setFormModalOpen(true);
  };

  const closeFormModal = () => {
    setFormModalOpen(false);
    setEditingDirecao(null);
  };

  const handleFormSuccess = () => {
    loadDirecoes();
  };

  // Estatísticas
  const totalDirecoes = direcoes.length;
  const direcoesComDiretor = direcoes.filter((d) => d.director).length;

  // Filtragem
  const filtered = direcoes.filter((d) => {
    const q = search.toLowerCase();
    return (
      !q ||
      d.descricao?.toLowerCase().includes(q) ||
      d.email?.toLowerCase().includes(q) ||
      d.director?.toLowerCase().includes(q)
    );
  });

  const columns = [
    {
      title: "Direção",
      dataIndex: "descricao",
      key: "descricao",
      width: 280,
      render: (descricao, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Avatar
            shape="square"
            size="large"
            style={{
              backgroundColor: "#e6f4ff",
              color: "#1677ff",
              borderRadius: 8,
            }}
            icon={<FaBuilding />}
          />
          <div>
            <div style={{ fontWeight: 600, color: "#1f1f1f", marginBottom: 4 }}>
              {descricao}
            </div>
            <Text type="secondary" style={{ fontSize: 13 }}>
              <FaEnvelope style={{ marginRight: 4 }} />
              {record.email || "—"}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: "Diretor",
      dataIndex: "director",
      key: "director",
      width: 220,
      render: (director) => (
        <Tag
          color={director ? "blue" : "default"}
          style={{
            borderRadius: 20,
            padding: "4px 12px",
            fontWeight: 500,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <FaUserTie />
          {director || "Não atribuído"}
        </Tag>
      ),
    },
    {
      title: "Ações",
      key: "actions",
      width: 100,
      fixed: "right",
      render: (_, record) => (
        <Tooltip title="Editar direção">
          <Button
            type="text"
            icon={<FaEdit />}
            onClick={() => openFormModal(record)}
            style={{ color: "#1677ff" }}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <Layout className="direcoes-layout" style={{ background: "transparent" }}>
      <Content style={{ padding: "24px 32px" }}>
        <ToastContainer position="top-right" autoClose={4000} />

        {/* Cabeçalho com gradiente */}
        <div
          style={{
            background: "linear-gradient(135deg, #1677ff 0%, #4096ff 100%)",
            borderRadius: 16,
            padding: "24px 32px",
            marginBottom: 32,
            boxShadow: "0 8px 20px rgba(22, 119, 255, 0.15)",
          }}
        >
          <Row align="middle" justify="space-between">
            <Col>
              <Space size="middle">
                <div
                  style={{
                    backgroundColor: "rgba(255,255,255,0.2)",
                    borderRadius: 12,
                    padding: 10,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaBuilding style={{ fontSize: 32, color: "#fff" }} />
                </div>
                <div>
                  <Title level={2} style={{ color: "#fff", margin: 0, fontWeight: 700 }}>
                    Gestão de Direções
                  </Title>
                  <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: 16 }}>
                    Organize as unidades estratégicas da empresa
                  </Text>
                </div>
              </Space>
            </Col>
            <Col>
              <Button
                type="primary"
                size="large"
                icon={<FaPlus />}
                onClick={() => openFormModal()}
                style={{
                  backgroundColor: "#fff",
                  color: "#1677ff",
                  border: "none",
                  fontWeight: 600,
                  borderRadius: 30,
                  padding: "0 24px",
                  height: 48,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                }}
              >
                Nova Direção
              </Button>
            </Col>
          </Row>
        </div>

        {/* Cards de estatísticas */}
        <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
          <Col xs={24} sm={12}>
            <Card
              className="stat-card"
              bordered={false}
              style={{
                borderRadius: 16,
                boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                border: "1px solid #f0f0f0",
              }}
              bodyStyle={{ padding: "20px 24px" }}
            >
              <Statistic
                title={<span style={{ fontSize: 14, color: "#8c8c8c" }}>Total de Direções</span>}
                value={totalDirecoes}
                valueStyle={{ fontSize: 32, fontWeight: 700, color: "#1677ff" }}
                prefix={
                  <div
                    style={{
                      backgroundColor: "#e6f4ff",
                      borderRadius: 12,
                      padding: 10,
                      marginRight: 16,
                    }}
                  >
                    <FaBuilding style={{ fontSize: 24, color: "#1677ff" }} />
                  </div>
                }
              />
            </Card>
          </Col>
          <Col xs={24} sm={12}>
            <Card
              className="stat-card"
              bordered={false}
              style={{
                borderRadius: 16,
                boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                border: "1px solid #f0f0f0",
              }}
              bodyStyle={{ padding: "20px 24px" }}
            >
              <Statistic
                title={<span style={{ fontSize: 14, color: "#8c8c8c" }}>Diretores Atribuídos</span>}
                value={direcoesComDiretor}
                valueStyle={{ fontSize: 32, fontWeight: 700, color: "#52c41a" }}
                prefix={
                  <div
                    style={{
                      backgroundColor: "#f6ffed",
                      borderRadius: 12,
                      padding: 10,
                      marginRight: 16,
                    }}
                  >
                    <FaUsers style={{ fontSize: 24, color: "#52c41a" }} />
                  </div>
                }
              />
            </Card>
          </Col>
        </Row>

        {/* Barra de pesquisa */}
        <Card
          bordered={false}
          style={{
            borderRadius: 16,
            marginBottom: 24,
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          }}
          bodyStyle={{ padding: "16px 24px" }}
        >
          <Row gutter={16} align="middle">
            <Col flex="auto">
              <Input
                size="large"
                prefix={<FaSearch style={{ color: "#bfbfbf" }} />}
                placeholder="Pesquisar por descrição, email ou diretor..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                allowClear
                style={{ borderRadius: 30 }}
              />
            </Col>
          </Row>
        </Card>

        {/* Tabela de direções */}
        <Card
          bordered={false}
          style={{
            borderRadius: 16,
            boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
            overflow: "hidden",
          }}
          bodyStyle={{ padding: 0 }}
        >
          <Table
            columns={columns}
            dataSource={filtered}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total de ${total} direções`,
              style: { padding: "16px 24px" },
            }}
            locale={{ emptyText: "Nenhuma direção encontrada" }}
            rowClassName={(_, index) =>
              index % 2 === 0 ? "table-row-light" : "table-row-dark"
            }
          />
        </Card>

        {/* Modal de formulário */}
        <DirecaoFormModal
          open={formModalOpen}
          onClose={closeFormModal}
          onSuccess={handleFormSuccess}
          editingDirecao={editingDirecao}
          userStored={userStored}
        />
      </Content>
    </Layout>
  );
};

export default Direcoes;